import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Check } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import Navbar from './landing/Navbar';
import Footer from './landing/Footer';
import { Container, GlassCard, fadeInUp } from './landing/shared';
import { trackEvent, AnalyticsEvents } from '../utils/analytics';

const BENEFIT_KEYS = ['benefit1', 'benefit2', 'benefit3', 'benefit4'];

const DemoRequest: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      await axios.post(API_ENDPOINTS.public.contact, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setSubmitStatus('success');
      setSubmitMessage(t('demo.form.success'));
      setFormData({ name: '', email: '', company: '', message: '' });
      trackEvent(AnalyticsEvents.DEMO_REQUEST, { status: 'success' });
    } catch (error: any) {
      setSubmitStatus('error');
      const errorMsg = error.response?.data?.error || error.message || t('demo.form.error');
      setSubmitMessage(errorMsg);
      trackEvent(AnalyticsEvents.DEMO_REQUEST, { status: 'error', error: error.response?.status });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-brand-gradient text-white">
      <Navbar />

      <main className="pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden pb-16 pt-16 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute left-1/2 top-[-10%] h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-brand-primary/20 blur-[140px]" />
            <div className="absolute right-[-10%] top-[20%] h-[350px] w-[450px] rounded-full bg-brand-secondary/10 blur-[120px]" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]" />
          </div>

          <Container className="relative text-center">
            <motion.span
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-secondary backdrop-blur-xl"
            >
              {t('landing.nav.cta')}
            </motion.span>

            <motion.h1
              variants={fadeInUp}
              custom={1}
              initial="hidden"
              animate="visible"
              className="mx-auto max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              {t('demo.title')}
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              custom={2}
              initial="hidden"
              animate="visible"
              className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-brand-muted sm:text-xl"
            >
              {t('demo.subtitle')}
            </motion.p>
          </Container>
        </section>

        {/* Form + Benefits */}
        <section className="relative py-16 sm:py-24">
          <Container>
            <div className="mx-auto max-w-4xl">
              <GlassCard index={0} className="p-8 sm:p-12">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white sm:text-3xl">{t('demo.form.title')}</h2>
                  <p className="mt-3 text-lg text-brand-muted">{t('demo.form.subtitle')}</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 flex items-start gap-3 rounded-xl border border-green-400/20 bg-green-500/10 p-5">
                    <CheckCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-400" />
                    <div>
                      <p className="font-semibold text-green-300">{submitMessage}</p>
                      <p className="mt-1 text-sm text-green-400/80">{t('demo.form.successMessage')}</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-400/20 bg-red-500/10 p-5">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
                    <p className="font-semibold text-red-300">{submitMessage}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-2 block text-sm font-medium text-brand-muted">
                        {t('demo.form.name')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                        placeholder={t('demo.form.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-brand-muted">
                        {t('demo.form.email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                        placeholder={t('demo.form.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="mb-2 block text-sm font-medium text-brand-muted">
                      {t('demo.form.company')} *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                      placeholder={t('demo.form.companyPlaceholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-2 block text-sm font-medium text-brand-muted">
                      {t('demo.form.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full resize-none rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                      placeholder={t('demo.form.messagePlaceholder')}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-button px-8 py-4 text-base font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {submitting ? (
                      t('demo.form.sending')
                    ) : (
                      <>
                        {t('demo.form.send')}
                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>

                {/* Benefits */}
                <div className="mt-12 border-t border-white/[0.08] pt-8">
                  <h3 className="mb-6 text-xl font-semibold text-white">{t('demo.benefits.title')}</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {BENEFIT_KEYS.map((key) => (
                      <div key={key} className="flex items-start gap-3">
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-button shadow-glow">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-white">{t(`demo.benefits.${key}`)}</p>
                          <p className="mt-1 text-sm text-brand-muted">{t(`demo.benefits.${key}Desc`)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </GlassCard>
            </div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DemoRequest;
